import { useEffect, useState } from "react";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/feature/SectionHeading";
import PostCard from "@/pages/blog/components/PostCard";
import { getRecentPosts, type BlogPost } from "@/pages/blog/blogData";

interface RecentPostsProps {
  title?: string;
  description?: string;
  posts?: BlogPost[];
  pageSize?: number;
}

export default function RecentPosts({
  title = "Artigos recentes",
  description = "Tudo que publicamos recentemente sobre hospedagem, desenvolvimento, SEO e marketing digital.",
  posts,
  pageSize = 6,
}: RecentPostsProps) {
  const list = posts ?? getRecentPosts();
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [posts]);

  const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
  const current = Math.min(page, totalPages);
  const start = (current - 1) * pageSize;
  const visible = list.slice(start, start + pageSize);

  const goTo = (target: number) => {
    setPage(target);
    const el = document.getElementById("artigos-recentes");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section id="artigos-recentes" className="bg-background-100 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <SectionHeading
          align="left"
          badge="Recentemente publicado"
          badgeIcon="ri-history-line"
          title={title}
          description={description}
        />

        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((post, index) => (
            <Reveal key={post.slug} delay={index * 40}>
              <PostCard post={post} />
            </Reveal>
          ))}
        </div>

        {totalPages > 1 ? (
          <nav
            aria-label="Paginação dos artigos"
            className="mt-10 flex flex-wrap items-center justify-center gap-2"
          >
            <button
              type="button"
              onClick={() => goTo(Math.max(1, current - 1))}
              disabled={current === 1}
              className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border px-4 py-2 text-sm font-semibold transition-colors ${
                current === 1
                  ? "cursor-not-allowed border-background-200 text-foreground-300"
                  : "border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
              }`}
            >
              <i className="ri-arrow-left-s-line" aria-hidden="true"></i>
              Anterior
            </button>

            {Array.from({ length: totalPages }).map((_, index) => {
              const number = index + 1;
              const active = number === current;
              return (
                <button
                  key={number}
                  type="button"
                  onClick={() => goTo(number)}
                  aria-current={active ? "page" : undefined}
                  className={`h-10 w-10 cursor-pointer rounded-md border text-sm font-semibold transition-colors ${
                    active
                      ? "border-primary-500 bg-primary-500 text-background-50"
                      : "border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                  }`}
                >
                  {number}
                </button>
              );
            })}

            <button
              type="button"
              onClick={() => goTo(Math.min(totalPages, current + 1))}
              disabled={current === totalPages}
              className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border px-4 py-2 text-sm font-semibold transition-colors ${
                current === totalPages
                  ? "cursor-not-allowed border-background-200 text-foreground-300"
                  : "border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
              }`}
            >
              Próxima
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            </button>
          </nav>
        ) : null}
      </div>
    </section>
  );
}